import { useEffect, useState } from '../lib/teact/teact';
import { getGlobal } from '../global';

import type { ApiUser } from '../api/types';
import { ApiMediaFormat } from '../api/types';

import { getChatAvatarHash } from '../global/helpers';
import { preloadImage } from '../util/files';
import { resizeImage } from '../util/imageResize';
import {
  ACCOUNT_INFO_CHANGED_EVENT, ACCOUNT_SLOT, getAccountsInfo, storeAccountData, updateAccountFromUser,
} from '../util/multiaccount';
import { parseInitialLocationHash } from '../util/routing';
import useMedia from './useMedia';

const REFRESH_INTERVAL = 60 * 1000;
const ACCOUNT_AVATAR_SIZE = 72;

export default function useMultiaccountInfo(currentUser?: ApiUser) {
  const [accounts, setAccounts] = useState(getAccountsInfo());
  const currentAvatarHash = currentUser && getChatAvatarHash(currentUser);
  const currentAvatarUrl = useMedia(currentAvatarHash, !currentAvatarHash, ApiMediaFormat.BlobUrl);

  useEffect(() => {
    if (currentUser && getGlobal().authRememberMe) {
      updateAccountFromUser(ACCOUNT_SLOT, currentUser);
      const initialLocationHash = parseInitialLocationHash();
      const isTest = process.env.NODE_ENV !== 'production' && (
        new URLSearchParams(window.location.search).get('test') === '1'
        || initialLocationHash?.tgWebAuthTest === '1'
      );
      storeAccountData(ACCOUNT_SLOT, { isTest });
    }

    setAccounts(getAccountsInfo());
  }, [currentUser?.id, currentUser?.firstName, currentUser?.lastName, currentUser?.phoneNumber,
    currentUser?.isPremium, currentUser?.color, currentUser]);

  useEffect(() => {
    let isCanceled = false;

    if (currentUser && getGlobal().authRememberMe) {
      if (!currentAvatarHash) {
        storeAccountData(ACCOUNT_SLOT, { avatarUri: undefined });
        setAccounts(getAccountsInfo());
      } else if (currentAvatarUrl && typeof currentAvatarUrl === 'string') {
        resizeImage(currentAvatarUrl, ACCOUNT_AVATAR_SIZE, ACCOUNT_AVATAR_SIZE)
          .then((resizedAvatar) => preloadImage(resizedAvatar))
          .then((img) => {
            if (isCanceled) return;

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d')!;
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            const avatarUri = canvas.toDataURL('image/png');

            storeAccountData(ACCOUNT_SLOT, { avatarUri });
            setAccounts(getAccountsInfo());
          });
      }
    }

    return () => {
      isCanceled = true;
    };
  }, [currentUser, currentAvatarHash, currentAvatarUrl]);

  useEffect(() => {
    const refresh = () => {
      if (currentUser && getGlobal().authRememberMe) updateAccountFromUser(ACCOUNT_SLOT, currentUser);
      setAccounts(getAccountsInfo());
    };
    const interval = window.setInterval(refresh, REFRESH_INTERVAL);
    const handleStorage = (event: StorageEvent) => {
      if (!event.key || event.key.startsWith('account')) refresh();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(ACCOUNT_INFO_CHANGED_EVENT, refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(ACCOUNT_INFO_CHANGED_EVENT, refresh);
    };
  }, [currentUser]);

  return accounts;
}
